/**
 * BOM Calculator Service
 *
 * Main orchestrator that integrates all calculation engines:
 * - Platform calculators (HGX, PCIe, NVL)
 * - Network fabric calculators
 * - Power calculators
 * - Validation engines
 *
 * Takes configuration state and returns complete BOM structure.
 */

import type { UIClusterConfiguration } from '../types/ui-configuration';
import type { BillOfMaterials, BOMLineItem, BOMSection } from '../types/bom';
import { getNodeById } from '../data/nodes';
import { generateUUID } from '../utils/uuid';

/**
 * Calculate complete BOM from configuration
 *
 * Note: Accepts UI configuration structure for compatibility with the UI layer
 */
export function calculateBOM(config: UIClusterConfiguration): BillOfMaterials {
  const { scaling, gpu, server, network, rack, power, additionalNodes } = config;

  // Calculate base metrics
  const nodes = Math.ceil(scaling.totalGPUs / scaling.gpusPerNode);

  // Calculate total rack units for all compute nodes
  const gpuNodeRackUnits = nodes * server.rackUnits;

  // Look up CPU node profile
  const cpuNodeProfile = additionalNodes.cpuNodes.enabled
    ? getNodeById(additionalNodes.cpuNodes.profileId)
    : null;
  const cpuNodeRackUnits =
    cpuNodeProfile && additionalNodes.cpuNodes.enabled
      ? additionalNodes.cpuNodes.count * cpuNodeProfile.rackUnits
      : 0;

  // Look up Infrastructure node profile
  const infraNodeProfile = additionalNodes.infraNodes.enabled
    ? getNodeById(additionalNodes.infraNodes.profileId)
    : null;
  const infraNodeRackUnits =
    infraNodeProfile && additionalNodes.infraNodes.enabled
      ? additionalNodes.infraNodes.count * infraNodeProfile.rackUnits
      : 0;

  const totalComputeRackUnits = gpuNodeRackUnits + cpuNodeRackUnits + infraNodeRackUnits;
  const computeRacks = Math.ceil(totalComputeRackUnits / rack.usableRackHeight);

  // Calculate network topology
  const networkResults = calculateNetworkTopology(config);

  // Calculate power
  const powerResults = calculatePower(config, networkResults);

  // Build BOM sections
  const sections: BOMSection[] = [
    buildComputeSection(config, nodes),
    buildEastWestNetworkSection(config, networkResults, nodes),
    buildNorthSouthNetworkSection(config, networkResults, nodes),
    buildManagementNetworkSection(config, networkResults),
    buildRackSection(config, computeRacks, networkResults),
  ];

  // Flatten all items
  const allItems: BOMLineItem[] = sections.flatMap((s) => Array.from(s.items));

  // Calculate summaries
  const compute = {
    totalGPUs: scaling.totalGPUs,
    gpuModel: gpu.platformName,
    totalNodes: nodes,
    totalCPUCores: nodes * server.cpuCount * 64, // Assume 64 cores per CPU
    totalMemoryGB: nodes * server.ramGB,
    totalGPUMemoryGB: scaling.totalGPUs * gpu.memoryGB,
  };

  const networkSummary = {
    totalSwitches:
      networkResults.ib.leaf +
      networkResults.ib.spine +
      networkResults.ns.leaf +
      networkResults.ns.spine +
      networkResults.mgmt.switches,
    switchBreakdown: {
      eastWestLeaf: networkResults.ib.leaf,
      eastWestSpine: networkResults.ib.spine,
      northSouthLeaf: networkResults.ns.leaf,
      northSouthSpine: networkResults.ns.spine,
      management: networkResults.mgmt.switches,
    },
    totalCables:
      networkResults.ib.cablesNodeLeaf +
      networkResults.ib.cablesLeafSpine +
      networkResults.ns.cablesNodeLeaf +
      networkResults.ns.cablesLeafSpine,
    cableBreakdown: {
      'IB Node-Leaf': networkResults.ib.cablesNodeLeaf,
      'IB Leaf-Spine': networkResults.ib.cablesLeafSpine,
      'NS Node-Leaf': networkResults.ns.cablesNodeLeaf,
      'NS Leaf-Spine': networkResults.ns.cablesLeafSpine,
    },
    totalBandwidthTbps: (networkResults.ib.totalLinks * network.eastWest.speedGbps) / 1000,
    bisectionBandwidthTbps:
      (networkResults.ib.leaf * networkResults.ib.uplinks * network.eastWest.speedGbps) / 1000,
  };

  const spaceSummary = {
    computeRacks,
    networkRacks: networkResults.switchRacks,
    storageRacks: 0,
    totalRacks: computeRacks + networkResults.switchRacks,
    totalRackUnitsUsed: totalComputeRackUnits + networkResults.totalSwitchRu,
    rackUtilization: (totalComputeRackUnits + networkResults.totalSwitchRu) / ((computeRacks + networkResults.switchRacks) * rack.standardRackHeight),
    floorSpaceSqFt: (computeRacks + networkResults.switchRacks) * 7, // 7 sq ft per rack
    floorSpaceSqM: (computeRacks + networkResults.switchRacks) * 0.65,
  };

  const powerSummary = {
    computePowerKw: powerResults.compute,
    networkPowerKw: powerResults.network,
    storagePowerKw: 0,
    totalITPowerKw: powerResults.totalIT,
    coolingOverheadKw: powerResults.cooling,
    totalFacilityPowerKw: powerResults.totalFacility,
    pue: power.coolingOverhead,
    peakPowerPerRackKw: powerResults.compute / computeRacks,
    avgPowerPerRackKw: powerResults.compute / computeRacks,
  };

  return {
    metadata: {
      bomId: generateUUID(),
      title: `GPU Cluster BOM - ${scaling.totalGPUs} GPUs`,
      description: config.description,
      configurationId: config.id,
      generatedAt: new Date(),
      version: config.app.version,
    },
    sections,
    allItems,
    summary: {
      compute,
      network: networkSummary,
      power: powerSummary,
      space: spaceSummary,
    },
    assumptions: [
      '2-tier (leaf-spine) fat-tree topology',
      `IB and NS downlink ratio applied uniformly (${network.eastWest.downlinkRatio})`,
      'Switch RU: IB=1U, NS=2U, OOB=1U',
      'Power figures from vendor datasheets - treat as estimates',
      `PUE: ${power.coolingOverhead}`,
    ],
    warnings: [],
  };
}

interface NetworkResults {
  ib: {
    totalLinks: number;
    leaf: number;
    spine: number;
    downlinks: number;
    uplinks: number;
    cablesNodeLeaf: number;
    cablesLeafSpine: number;
  };
  ns: {
    totalLinks: number;
    leaf: number;
    spine: number;
    downlinks: number;
    uplinks: number;
    cablesNodeLeaf: number;
    cablesLeafSpine: number;
  };
  mgmt: {
    switches: number;
    totalEndpoints: number;
  };
  totalSwitchRu: number;
  switchRacks: number;
}

function calculateNetworkTopology(config: UIClusterConfiguration): NetworkResults {
  const { scaling, network, additionalNodes, server } = config;
  const gpuNodes = Math.ceil(scaling.totalGPUs / scaling.gpusPerNode);

  // Calculate additional node counts
  const cpuNodeCount = additionalNodes.cpuNodes.enabled ? additionalNodes.cpuNodes.count : 0;
  const infraNodeCount = additionalNodes.infraNodes.enabled ? additionalNodes.infraNodes.count : 0;
  const totalNodes = gpuNodes + cpuNodeCount + infraNodeCount;

  // Look up node profiles for Ethernet port counts
  const cpuNodeProfile = additionalNodes.cpuNodes.enabled
    ? getNodeById(additionalNodes.cpuNodes.profileId)
    : null;
  const infraNodeProfile = additionalNodes.infraNodes.enabled
    ? getNodeById(additionalNodes.infraNodes.profileId)
    : null;

  // East/West InfiniBand - using blocking factor (GPU nodes only, CPU/Infra typically don't need IB)
  const ibBlockingFactor = network.eastWest.blockingFactor || 1.0; // Default to non-blocking
  const ibTotalPorts = network.eastWest.leafSwitch.ports;
  const ibDownlinks = Math.floor(ibTotalPorts * ibBlockingFactor / (ibBlockingFactor + 1));
  const ibUplinks = ibTotalPorts - ibDownlinks;

  const ibTotalLinks = gpuNodes * network.eastWest.portsPerNode;
  const ibMinLeafsForCapacity = Math.ceil(ibTotalLinks / ibDownlinks);
  const ibMinLeafsForDiversity = network.eastWest.portsPerNode; // Ensure each link to different leaf
  const ibLeaf = Math.max(ibMinLeafsForCapacity, ibMinLeafsForDiversity);

  // Calculate spine switches based on topology type
  let ibSpine: number;
  let ibCablesLeafSpine: number;
  const ibTopologyType = network.eastWest.topologyType || 'fat-tree';

  if (ibTopologyType === 'rail-optimized') {
    // Rail-optimized: Each rail has dedicated spine switches
    const ibRailsCount = network.eastWest.railsCount || 4;
    const ibSpinePorts = network.eastWest.spineSwitch.ports;
    const ibSpinesPerRail = Math.ceil(ibUplinks / ibSpinePorts);
    ibSpine = ibRailsCount * ibSpinesPerRail;
    // Each leaf connects to all spines in each rail
    ibCablesLeafSpine = ibLeaf * ibUplinks;
  } else {
    // Traditional fat-tree: Shared spine layer using bundle size approach (ClusterDesign.org)
    const ibSpinePorts = network.eastWest.spineSwitch.ports;
    const ibBundleSize = Math.floor(ibSpinePorts / ibLeaf);
    ibSpine = ibBundleSize >= 1 ? Math.ceil(ibUplinks / ibBundleSize) : Math.ceil(ibLeaf);
    ibCablesLeafSpine = ibLeaf * ibUplinks;
  }

  const ibCablesNodeLeaf = ibTotalLinks; // 1:1 mapping to server IB ports

  // North/South Ethernet - using blocking factor (includes ALL nodes: GPU + CPU + Infrastructure)
  const nsBlockingFactor = network.northSouth.blockingFactor || 1.0; // Default to non-blocking
  const nsTotalPorts = network.northSouth.leafSwitch.ports;
  const nsDownlinks = Math.floor(nsTotalPorts * nsBlockingFactor / (nsBlockingFactor + 1));
  const nsUplinks = nsTotalPorts - nsDownlinks;

  // Calculate total Ethernet ports needed from all nodes (per-node Ethernet ports)
  const gpuNodeEthernetPorts = gpuNodes * server.ethernetPorts;
  const cpuNodeEthernetPorts = cpuNodeProfile && additionalNodes.cpuNodes.enabled
    ? additionalNodes.cpuNodes.count * cpuNodeProfile.ethernetPorts
    : 0;
  const infraNodeEthernetPorts = infraNodeProfile && additionalNodes.infraNodes.enabled
    ? additionalNodes.infraNodes.count * infraNodeProfile.ethernetPorts
    : 0;
  const nsTotalLinks = gpuNodeEthernetPorts + cpuNodeEthernetPorts + infraNodeEthernetPorts;

  const nsMinLeafsForCapacity = Math.ceil(nsTotalLinks / nsDownlinks);
  // Use max Ethernet ports per node for diversity requirement
  const maxEthernetPortsPerNode = Math.max(
    server.ethernetPorts,
    cpuNodeProfile?.ethernetPorts || 0,
    infraNodeProfile?.ethernetPorts || 0
  );
  const nsMinLeafsForDiversity = maxEthernetPortsPerNode; // Ensure each link to different leaf
  const nsLeaf = Math.max(nsMinLeafsForCapacity, nsMinLeafsForDiversity);

  // Calculate spine switches based on topology type
  let nsSpine: number;
  let nsCablesLeafSpine: number;
  const topologyType = network.northSouth.topologyType || 'fat-tree';

  if (topologyType === 'rail-optimized') {
    // Rail-optimized: Each rail has dedicated spine switches
    const railsCount = network.northSouth.railsCount || 4;
    const spinesPerRail = Math.ceil(nsUplinks / network.northSouth.spineSwitch.ports);
    nsSpine = railsCount * spinesPerRail;
    // Each leaf connects to all spines in each rail
    nsCablesLeafSpine = nsLeaf * nsUplinks;
  } else {
    // Traditional fat-tree: Shared spine layer
    nsSpine = Math.ceil((nsLeaf * nsUplinks) / network.northSouth.spineSwitch.ports);
    nsCablesLeafSpine = nsLeaf * nsUplinks;
  }

  // Apply High Availability multiplier for spine switches
  const nsHaMultiplier = network.northSouth.highAvailabilitySpine ? 2 : 1;
  nsSpine = nsSpine * nsHaMultiplier;
  nsCablesLeafSpine = nsCablesLeafSpine * nsHaMultiplier;

  const nsCablesNodeLeaf = nsTotalLinks; // Total Ethernet cables = sum of all node Ethernet ports

  // Management OOB - includes all nodes: GPU + CPU + Infrastructure
  const oobHost = totalNodes;
  const oobDpu = totalNodes * network.management.dpusPerNode * network.management.oobPortsPerDPU;
  const oobSwitches = ibLeaf + ibSpine + nsLeaf + nsSpine;
  const oobTotal = oobHost + oobDpu + oobSwitches;
  const mgmtSwitches = Math.ceil(oobTotal / network.management.portsPerSwitch);

  // Rack units for switches
  const ibSwitchRu = (ibLeaf + ibSpine) * 1;
  const nsSwitchRu = (nsLeaf + nsSpine) * 2;
  const mgmtSwitchRu = mgmtSwitches * 1;
  const cableMgmtRu = Math.ceil((ibLeaf + ibSpine + nsLeaf + nsSpine + mgmtSwitches) * 0.5);
  const totalSwitchRu = ibSwitchRu + nsSwitchRu + mgmtSwitchRu + cableMgmtRu;
  const switchRacks = Math.ceil(totalSwitchRu / 48);

  return {
    ib: {
      totalLinks: ibTotalLinks,
      leaf: ibLeaf,
      spine: ibSpine,
      downlinks: ibDownlinks,
      uplinks: ibUplinks,
      cablesNodeLeaf: ibCablesNodeLeaf,
      cablesLeafSpine: ibCablesLeafSpine,
    },
    ns: {
      totalLinks: nsTotalLinks,
      leaf: nsLeaf,
      spine: nsSpine,
      downlinks: nsDownlinks,
      uplinks: nsUplinks,
      cablesNodeLeaf: nsCablesNodeLeaf,
      cablesLeafSpine: nsCablesLeafSpine,
    },
    mgmt: {
      switches: mgmtSwitches,
      totalEndpoints: oobTotal,
    },
    totalSwitchRu,
    switchRacks,
  };
}

interface PowerResults {
  compute: number;
  network: number;
  totalIT: number;
  cooling: number;
  totalFacility: number;
}

function calculatePower(config: UIClusterConfiguration, network: NetworkResults): PowerResults {
  const { scaling, power: powerConfig, additionalNodes } = config;
  const nodes = Math.ceil(scaling.totalGPUs / scaling.gpusPerNode);

  // Calculate power from all compute node types
  const gpuNodePower = nodes * powerConfig.powerPerNodeKw;

  // Look up CPU node profile for power
  const cpuNodeProfile = additionalNodes.cpuNodes.enabled
    ? getNodeById(additionalNodes.cpuNodes.profileId)
    : null;
  const cpuNodePower =
    cpuNodeProfile && additionalNodes.cpuNodes.enabled
      ? additionalNodes.cpuNodes.count * cpuNodeProfile.powerKw
      : 0;

  // Look up Infrastructure node profile for power
  const infraNodeProfile = additionalNodes.infraNodes.enabled
    ? getNodeById(additionalNodes.infraNodes.profileId)
    : null;
  const infraNodePower =
    infraNodeProfile && additionalNodes.infraNodes.enabled
      ? additionalNodes.infraNodes.count * infraNodeProfile.powerKw
      : 0;

  const compute = gpuNodePower + cpuNodePower + infraNodePower;

  let networkPower = 0;
  if (powerConfig.includeSwitchPower) {
    const ibSwitches = network.ib.leaf + network.ib.spine;
    const nsSwitches = network.ns.leaf + network.ns.spine;
    networkPower = (ibSwitches * 0.85 + nsSwitches * 0.65 + network.mgmt.switches * 0.3);
  }

  const totalIT = compute + networkPower;
  const cooling = totalIT * (powerConfig.coolingOverhead - 1);
  const totalFacility = totalIT * powerConfig.coolingOverhead;

  return {
    compute,
    network: networkPower,
    totalIT,
    cooling,
    totalFacility,
  };
}

function buildComputeSection(config: UIClusterConfiguration, nodes: number): BOMSection {
  const { scaling, gpu, server, additionalNodes } = config;

  const items: BOMLineItem[] = [
    {
      lineNumber: 1,
      category: 'compute',
      name: gpu.platformName,
      description: `${gpu.family} GPU with ${gpu.memoryGB}GB ${gpu.memoryType}`,
      quantity: scaling.totalGPUs,
      unit: 'each',
      quantityPerNode: scaling.gpusPerNode,
      derivation: `${nodes} nodes × ${scaling.gpusPerNode} GPUs/node`,
      specifications: {
        tdpWatts: gpu.tdpWatts,
        memoryGB: gpu.memoryGB,
        interconnect: gpu.interconnect,
      },
    },
    {
      lineNumber: 2,
      category: 'compute',
      name: `${server.manufacturer} ${server.model}`,
      description: `GPU compute server - ${server.formFactor}`,
      quantity: nodes,
      unit: 'each',
      quantityPerNode: 1,
      derivation: `ceil(${scaling.totalGPUs} GPUs ÷ ${scaling.gpusPerNode} GPUs/node)`,
      specifications: {
        rackUnits: server.rackUnits,
        cpuCount: server.cpuCount,
        ramGB: server.ramGB,
      },
    },
  ];

  // Add CPU-only nodes if enabled
  const cpuNodeProfile = additionalNodes.cpuNodes.enabled
    ? getNodeById(additionalNodes.cpuNodes.profileId)
    : null;

  if (cpuNodeProfile && additionalNodes.cpuNodes.enabled && additionalNodes.cpuNodes.count > 0) {
    items.push({
      lineNumber: 3,
      category: 'compute',
      name: cpuNodeProfile.name,
      description:
        additionalNodes.cpuNodes.descriptionOverride || cpuNodeProfile.description,
      quantity: additionalNodes.cpuNodes.count,
      unit: 'each',
      quantityPerNode: 1,
      derivation: 'User-specified quantity',
      specifications: {
        rackUnits: cpuNodeProfile.rackUnits,
        powerKw: cpuNodeProfile.powerKw,
      },
    });
  }

  // Add Infrastructure nodes if enabled
  const infraNodeProfile = additionalNodes.infraNodes.enabled
    ? getNodeById(additionalNodes.infraNodes.profileId)
    : null;

  if (
    infraNodeProfile &&
    additionalNodes.infraNodes.enabled &&
    additionalNodes.infraNodes.count > 0
  ) {
    items.push({
      lineNumber: 4,
      category: 'compute',
      name: infraNodeProfile.name,
      description:
        additionalNodes.infraNodes.descriptionOverride || infraNodeProfile.description,
      quantity: additionalNodes.infraNodes.count,
      unit: 'each',
      quantityPerNode: 1,
      derivation: 'User-specified quantity',
      specifications: {
        rackUnits: infraNodeProfile.rackUnits,
        powerKw: infraNodeProfile.powerKw,
      },
    });
  }

  // Calculate totals including additional nodes
  const cpuNodePower =
    cpuNodeProfile && additionalNodes.cpuNodes.enabled
      ? additionalNodes.cpuNodes.count * cpuNodeProfile.powerKw
      : 0;
  const infraNodePower =
    infraNodeProfile && additionalNodes.infraNodes.enabled
      ? additionalNodes.infraNodes.count * infraNodeProfile.powerKw
      : 0;
  const cpuNodeRackUnits =
    cpuNodeProfile && additionalNodes.cpuNodes.enabled
      ? additionalNodes.cpuNodes.count * cpuNodeProfile.rackUnits
      : 0;
  const infraNodeRackUnits =
    infraNodeProfile && additionalNodes.infraNodes.enabled
      ? additionalNodes.infraNodes.count * infraNodeProfile.rackUnits
      : 0;

  const totalNodes = nodes +
    (additionalNodes.cpuNodes.enabled ? additionalNodes.cpuNodes.count : 0) +
    (additionalNodes.infraNodes.enabled ? additionalNodes.infraNodes.count : 0);

  return {
    id: 'compute',
    title: '1 - Compute Cluster',
    category: 'compute',
    items,
    totals: {
      quantity: totalNodes,
      power: nodes * config.power.powerPerNodeKw + cpuNodePower + infraNodePower,
      rackUnits: nodes * server.rackUnits + cpuNodeRackUnits + infraNodeRackUnits,
    },
  };
}

function buildEastWestNetworkSection(
  config: UIClusterConfiguration,
  network: NetworkResults,
  nodes: number
): BOMSection {
  const { network: netConfig } = config;

  const items: BOMLineItem[] = [
    {
      lineNumber: 10,
      category: 'network-ew',
      name: `${netConfig.eastWest.leafSwitch.model} (Leaf)`,
      description: `${netConfig.eastWest.speedGbps}Gb/s ${netConfig.eastWest.fabricType} leaf switch`,
      quantity: network.ib.leaf,
      unit: 'each',
      derivation: `ceil(${network.ib.totalLinks} node ports ÷ ${network.ib.downlinks} downlinks/switch)`,
      specifications: {
        ports: netConfig.eastWest.leafSwitch.ports,
        formFactor: netConfig.eastWest.leafSwitch.formFactor,
        powerWatts: netConfig.eastWest.leafSwitch.powerWatts,
      },
    },
    {
      lineNumber: 11,
      category: 'network-ew',
      name: `${netConfig.eastWest.spineSwitch.model} (Spine)`,
      description: `${netConfig.eastWest.speedGbps}Gb/s ${netConfig.eastWest.fabricType} spine switch${(netConfig.eastWest.topologyType === 'rail-optimized') ? ' (rail-optimized)' : ''}`,
      quantity: network.ib.spine,
      unit: 'each',
      derivation: (netConfig.eastWest.topologyType === 'rail-optimized')
        ? `${netConfig.eastWest.railsCount || 4} rails × ceil(${network.ib.uplinks} uplinks ÷ ${netConfig.eastWest.spineSwitch.ports} ports)`
        : `ceil(${network.ib.leaf * network.ib.uplinks} leaf uplinks ÷ ${netConfig.eastWest.spineSwitch.ports} ports/switch)`,
      specifications: {
        ports: netConfig.eastWest.spineSwitch.ports,
        formFactor: netConfig.eastWest.spineSwitch.formFactor,
        powerWatts: netConfig.eastWest.spineSwitch.powerWatts,
      },
    },
    {
      lineNumber: 12,
      category: 'cable',
      name: `${netConfig.eastWest.fabricType} ${netConfig.eastWest.speedGbps}Gb/s Cable - Node to Leaf`,
      description: 'DAC or AOC cables for node to leaf connections',
      quantity: network.ib.cablesNodeLeaf,
      unit: 'each',
      derivation: `${network.ib.leaf} leaf × ${network.ib.downlinks} downlinks (full switch fill; ${nodes * netConfig.eastWest.portsPerNode} used)`,
    },
    {
      lineNumber: 13,
      category: 'cable',
      name: `${netConfig.eastWest.fabricType} ${netConfig.eastWest.speedGbps}Gb/s Cable - Leaf to Spine`,
      description: 'DAC or AOC cables for leaf to spine connections',
      quantity: network.ib.cablesLeafSpine,
      unit: 'each',
      derivation: `${network.ib.leaf} leaf × ${network.ib.uplinks} uplinks`,
    },
  ];

  return {
    id: 'network-ew',
    title: '2 - East/West Fabric (Training Network)',
    category: 'network-ew',
    items,
    totals: {
      quantity: network.ib.leaf + network.ib.spine,
      power: (network.ib.leaf + network.ib.spine) * (netConfig.eastWest.leafSwitch.powerWatts / 1000),
    },
  };
}

function buildNorthSouthNetworkSection(
  config: UIClusterConfiguration,
  network: NetworkResults,
  nodes: number
): BOMSection {
  const { network: netConfig, additionalNodes, server } = config;

  // Calculate total nodes including CPU and Infrastructure
  const cpuNodeCount = additionalNodes.cpuNodes.enabled ? additionalNodes.cpuNodes.count : 0;
  const infraNodeCount = additionalNodes.infraNodes.enabled ? additionalNodes.infraNodes.count : 0;
  const totalNodes = nodes + cpuNodeCount + infraNodeCount;

  // Get node profiles for Ethernet port counts
  const cpuNodeProfile = additionalNodes.cpuNodes.enabled
    ? getNodeById(additionalNodes.cpuNodes.profileId)
    : null;
  const infraNodeProfile = additionalNodes.infraNodes.enabled
    ? getNodeById(additionalNodes.infraNodes.profileId)
    : null;

  // Build derivation string for cable count
  const derivationParts: string[] = [];
  derivationParts.push(`${nodes} GPU nodes × ${server.ethernetPorts} ports`);
  if (cpuNodeCount > 0 && cpuNodeProfile) {
    derivationParts.push(`${cpuNodeCount} CPU nodes × ${cpuNodeProfile.ethernetPorts} ports`);
  }
  if (infraNodeCount > 0 && infraNodeProfile) {
    derivationParts.push(`${infraNodeCount} Infra nodes × ${infraNodeProfile.ethernetPorts} ports`);
  }
  const cableDerivation = derivationParts.join(' + ');

  const items: BOMLineItem[] = [
    {
      lineNumber: 20,
      category: 'network-ns',
      name: `${netConfig.northSouth.leafSwitch.model} (Leaf)`,
      description: `${netConfig.northSouth.speedGbps}Gb/s ${netConfig.northSouth.fabricType} leaf switch`,
      quantity: network.ns.leaf,
      unit: 'each',
      derivation: `ceil(${network.ns.totalLinks} node ports ÷ ${network.ns.downlinks} downlinks/switch)`,
      specifications: {
        ports: netConfig.northSouth.leafSwitch.ports,
        formFactor: netConfig.northSouth.leafSwitch.formFactor,
        powerWatts: netConfig.northSouth.leafSwitch.powerWatts,
      },
    },
    {
      lineNumber: 21,
      category: 'network-ns',
      name: `${netConfig.northSouth.spineSwitch.model} (Spine)`,
      description: `${netConfig.northSouth.speedGbps}Gb/s ${netConfig.northSouth.fabricType} spine switch${(netConfig.northSouth.topologyType === 'rail-optimized') ? ' (rail-optimized)' : ''}${netConfig.northSouth.highAvailabilitySpine ? ' (HA)' : ''}`,
      quantity: network.ns.spine,
      unit: 'each',
      derivation: (() => {
        const baseCalc = (netConfig.northSouth.topologyType === 'rail-optimized')
          ? `${netConfig.northSouth.railsCount || 4} rails × ceil(${network.ns.uplinks} uplinks ÷ ${netConfig.northSouth.spineSwitch.ports} ports)`
          : `ceil(${network.ns.leaf * network.ns.uplinks} leaf uplinks ÷ ${netConfig.northSouth.spineSwitch.ports} ports/switch)`;
        return netConfig.northSouth.highAvailabilitySpine ? `(${baseCalc}) × 2 for HA` : baseCalc;
      })(),
      specifications: {
        ports: netConfig.northSouth.spineSwitch.ports,
        formFactor: netConfig.northSouth.spineSwitch.formFactor,
        powerWatts: netConfig.northSouth.spineSwitch.powerWatts,
      },
    },
    {
      lineNumber: 22,
      category: 'cable',
      name: `${netConfig.northSouth.fabricType} ${netConfig.northSouth.speedGbps}Gb/s Cable - Node to Leaf`,
      description: 'Ethernet cables for node to leaf connections',
      quantity: network.ns.cablesNodeLeaf,
      unit: 'each',
      derivation: cableDerivation,
    },
    {
      lineNumber: 23,
      category: 'cable',
      name: `${netConfig.northSouth.fabricType} ${netConfig.northSouth.speedGbps}Gb/s Cable - Leaf to Spine`,
      description: 'Ethernet cables for leaf to spine connections',
      quantity: network.ns.cablesLeafSpine,
      unit: 'each',
      derivation: `${network.ns.leaf} leaf × ${network.ns.uplinks} uplinks`,
    },
  ];

  return {
    id: 'network-ns',
    title: '3 - North/South Fabric (Data Network)',
    category: 'network-ns',
    items,
    totals: {
      quantity: network.ns.leaf + network.ns.spine,
      power: (network.ns.leaf + network.ns.spine) * (netConfig.northSouth.leafSwitch.powerWatts / 1000),
    },
  };
}

function buildManagementNetworkSection(
  config: UIClusterConfiguration,
  network: NetworkResults,
): BOMSection {
  const { network: netConfig } = config;

  const items: BOMLineItem[] = [
    {
      lineNumber: 30,
      category: 'network-mgmt',
      name: netConfig.management.switchModel,
      description: 'OOB management switches',
      quantity: network.mgmt.switches,
      unit: 'each',
      derivation: `ceil(${network.mgmt.totalEndpoints} OOB endpoints ÷ ${netConfig.management.portsPerSwitch} ports/switch)`,
      specifications: {
        ports: netConfig.management.portsPerSwitch,
      },
    },
  ];

  return {
    id: 'network-mgmt',
    title: '4 - OOB / Management Network',
    category: 'network-mgmt',
    items,
    totals: {
      quantity: network.mgmt.switches,
    },
  };
}

function buildRackSection(
  config: UIClusterConfiguration,
  computeRacks: number,
  network: NetworkResults
): BOMSection {
  const { scaling, server, rack, additionalNodes } = config;
  const nodes = Math.ceil(scaling.totalGPUs / scaling.gpusPerNode);

  // Calculate rack units for derivation
  const gpuNodeRackUnits = nodes * server.rackUnits;

  // Look up CPU node profile
  const cpuNodeProfile = additionalNodes.cpuNodes.enabled
    ? getNodeById(additionalNodes.cpuNodes.profileId)
    : null;
  const cpuNodeRackUnits =
    cpuNodeProfile && additionalNodes.cpuNodes.enabled
      ? additionalNodes.cpuNodes.count * cpuNodeProfile.rackUnits
      : 0;

  // Look up Infrastructure node profile
  const infraNodeProfile = additionalNodes.infraNodes.enabled
    ? getNodeById(additionalNodes.infraNodes.profileId)
    : null;
  const infraNodeRackUnits =
    infraNodeProfile && additionalNodes.infraNodes.enabled
      ? additionalNodes.infraNodes.count * infraNodeProfile.rackUnits
      : 0;

  const totalComputeRackUnits = gpuNodeRackUnits + cpuNodeRackUnits + infraNodeRackUnits;

  const items: BOMLineItem[] = [
    {
      lineNumber: 40,
      category: 'rack',
      name: 'Compute Rack',
      description: `Standard ${rack.standardRackHeight}U rack for compute servers (GPU, CPU, and infrastructure nodes)`,
      quantity: computeRacks,
      unit: 'rack',
      derivation: `ceil(${totalComputeRackUnits}U total compute ÷ ${rack.usableRackHeight}U usable)`,
    },
    {
      lineNumber: 41,
      category: 'rack',
      name: 'Network Rack',
      description: 'Standard 48U rack for network switches',
      quantity: network.switchRacks,
      unit: 'rack',
      derivation: `ceil(${network.totalSwitchRu} switch RU ÷ 48U)`,
    },
  ];

  return {
    id: 'rack',
    title: '5 - Rack Layout & Infrastructure',
    category: 'rack',
    items,
    totals: {
      quantity: computeRacks + network.switchRacks,
    },
  };
}
